import { useCallback, useEffect, useState } from "react";
import { L3ServiceLocationViewModel, MaintenanceStatus } from "../../model";
import { useNetworkServiceEventsApi } from "../../../l3-services/api/NetworkServiceEvents/NetworkServiceEvents.api";
import {
  EventRegistrationScope,
  MaintenanceUpdate,
  MaintenanceWork,
  MaintenanceWorkStatus,
  NetworkServiceScopeUpdate,
  NetworkServiceUpdateEvent,
  NotificationUpdate,
  StatusUpdate
} from "../../../l3-services/model";
import { parseUpdateEvent } from "../../../l3-services/components/L3ServiceList/L3ServiceList.utils";

type TopologyNetworkServiceEventsHookResult = {
  locations: L3ServiceLocationViewModel[] | null;
}

const buildUpdatedLocationIfCorrespondingEventFound = (
  location: L3ServiceLocationViewModel,
  events: NetworkServiceUpdateEvent[]
): L3ServiceLocationViewModel => {
  const locationCorrespondingEvent = findLocationCorrespondingEvent(events, location.itemId);
  if (locationCorrespondingEvent === null) {
    return location;
  } else {
    const statusUpdate = getStatusUpdate(locationCorrespondingEvent);
    const maintenanceUpdate = getMaintenanceUpdate(locationCorrespondingEvent);
    const notificationUpdate = getNotificationUpdate(locationCorrespondingEvent);
    return {
      ...location,
      serviceStatus: statusUpdate === undefined ? location.serviceStatus : statusUpdate.status,
      maintenanceStatuses: maintenanceUpdate === undefined ? location.maintenanceStatuses : maintenanceUpdate.maintenanceWorks.map(getMaintenanceStatus),
      notificationCount: notificationUpdate === undefined ? location.notificationCount : notificationUpdate.count
    }
  }
};

const findLocationCorrespondingEvent = (
  events: NetworkServiceUpdateEvent[],
  itemId: number
): NetworkServiceUpdateEvent|null => {
  return events.find(event => event.networkServiceId === itemId) || null;
};

const getStatusUpdate = (event: NetworkServiceUpdateEvent): StatusUpdate => {
  const statusScopeUpdate = findEventScopeUpdate(event, EventRegistrationScope.Status);
  return statusScopeUpdate?.content as StatusUpdate;
};

const getMaintenanceUpdate = (event: NetworkServiceUpdateEvent): MaintenanceUpdate => {
  const maintenanceScopeUpdate = findEventScopeUpdate(event, EventRegistrationScope.Maintenance);
  return maintenanceScopeUpdate?.content as MaintenanceUpdate;
};

const getNotificationUpdate = (event: NetworkServiceUpdateEvent): NotificationUpdate => {
    const notificationScopeUpdate = findEventScopeUpdate(event, EventRegistrationScope.Notification)
    return notificationScopeUpdate?.content as NotificationUpdate;
}

const findEventScopeUpdate = (event: NetworkServiceUpdateEvent, scope: EventRegistrationScope): NetworkServiceScopeUpdate | undefined => {
  return event.scopeUpdates.find(scopeUpdate => scopeUpdate.scope === scope);
};

const getMaintenanceStatus = (maintenanceWork: MaintenanceWork): MaintenanceStatus => {
  switch (maintenanceWork.status) {
    case MaintenanceWorkStatus.Ongoing : return MaintenanceStatus.Active;
    case MaintenanceWorkStatus.Planned : return MaintenanceStatus.Planned;
  }
};

export const useTopologyNetworkServiceEvents = (
  customerId: number | null | undefined,
  fetchedLocations: L3ServiceLocationViewModel[] | null,
  isLocationListFetched: boolean
): TopologyNetworkServiceEventsHookResult => {
  const [locations, setLocations] = useState<L3ServiceLocationViewModel[]|null>(null);
  const { sendJsonMessage, lastMessage, isConnected } = useNetworkServiceEventsApi();

  const registerServicesObservation = useCallback((): void => {
    const customerNetworkServicesEventRegistration = {
      eventRegistration: {
        customerId,
        eventRegistrationScopes: [
          EventRegistrationScope.Maintenance,
          EventRegistrationScope.Status,
          EventRegistrationScope.Notification,
        ]
      }
    };
    sendJsonMessage(customerNetworkServicesEventRegistration);
  }, [customerId, sendJsonMessage]);

  useEffect(() => {
    if (isLocationListFetched) {
      if (locations === null) {
        setLocations(fetchedLocations);
      }
      if (customerId !== undefined && isConnected) {
        registerServicesObservation();
      }
    }
  }, [customerId, fetchedLocations, isLocationListFetched, isConnected, registerServicesObservation]);

  useEffect(() => {
    if (lastMessage !== undefined && lastMessage !== null) {
      const message = lastMessage.data;
      const events = parseUpdateEvent(message);
      if (events !== null) {
        setLocations(locations => locations === null
          ? locations
          : locations.map(location => buildUpdatedLocationIfCorrespondingEventFound(location, events)));
      }
    }
  }, [lastMessage]);

  return {
    locations
  };
}